// PE Delivery Loop — Supabase-direct content reads for the lesson step shells.
//
// Mirrors peProgress.ts: the step surfaces (Vocab / Episode / Activity) read
// their content straight from the authenticated Supabase client (all three
// tables have authenticated SELECT RLS), so no backend/held-file changes are
// needed. Read Along (SceneReader) keeps using the serving API unchanged.

import supabase from "../utils/supabase";
import type { Lang, Tier } from "./serving";

/** Base of the serving API — same value the live serving layer uses. */
const SERVING_BASE =
  (import.meta.env.VITE_SERVING_API_BASE as string | undefined) ?? "/api/v1/pe";

/**
 * Resolve a raw R2 asset key (e.g. "scenes/s1/ch01/t1/sc15.png") to a loadable
 * URL via the SAME public proxy SceneReader's images come through
 * (GET /pe/scenes/asset?key=…). Slashes are preserved; segments are encoded so
 * keys with spaces still resolve.
 */
export function peSceneAssetUrl(key: string): string {
  const safe = key.split("/").map(encodeURIComponent).join("/");
  return `${SERVING_BASE}/scenes/asset?key=${safe}`;
}

// ---------------------------------------------------------------------------
// Vocabulary — Apply cards only (role='apply' is the student set; launch is
// teacher-only per the §2 7/6 split).
//
// Run E2 J5 (D02): the card headword is a real word from pe_concept_lexemes in
// the lesson language, never the raw pe_concepts.slug ("market_research"):
//   - ES: the concept's primary Spanish lexeme (lowest id, the same rule the
//     peCurriculum.ts generator uses for word_es).
//   - EN: the English lexeme that spells the slug, else the slug with spaces.
// Underscores never reach the screen. No ES lexeme -> the EN headword (the
// export has an ES lexeme for every concept a chapter uses).
// ---------------------------------------------------------------------------

interface ConceptRow {
  slug?: string | null;
  pe_concept_lexemes?: { id: number; language: string; term: string }[] | null;
}

const VOCAB_COLS =
  "id, tier, role, definition_en, definition_es, example_sentence_en, example_sentence_es, image_url, display_order, concept_id";
const CONCEPT_EMBED = "pe_concepts ( slug, pe_concept_lexemes ( id, language, term ) )";
const CONCEPT_EMBED_SLUG_ONLY = "pe_concepts ( slug )";

const spaced = (s: string): string => s.replace(/_/g, " ").replace(/\s+/g, " ").trim();

/** Display headword for a vocab card in the lesson language (see above). */
export function conceptHeadword(lang: Lang, concept: ConceptRow | null | undefined): string {
  const slug = spaced(concept?.slug ?? "");
  const lexemes = [...(concept?.pe_concept_lexemes ?? [])].sort((a, b) => a.id - b.id);
  const en =
    lexemes.find((l) => l.language === "en" && spaced(l.term).toLowerCase() === slug.toLowerCase())
      ?.term ?? slug;
  if (lang === "es") {
    const es = lexemes.find((l) => l.language === "es" && l.term?.trim());
    if (es) return spaced(es.term);
  }
  return spaced(en);
}

/**
 * Read vocab rows with the concept lexemes embedded. If the lexeme embed fails
 * (older RLS / schema), fall back to the slug-only read so the step never
 * breaks; the headword is then the slug with spaces.
 */
async function selectVocab(build: (cols: string) => PromiseLike<{ data: unknown; error: unknown }>) {
  let { data, error } = await build(`${VOCAB_COLS}, ${CONCEPT_EMBED}`);
  if (error) ({ data, error } = await build(`${VOCAB_COLS}, ${CONCEPT_EMBED_SLUG_ONLY}`));
  if (error) throw error;
  return (data ?? []) as any[];
}

export interface VocabCard {
  id: number;
  word: string;
  definition: string | null;
  example: string | null;
  imageUrl: string | null;
}

export async function fetchVocabApply(
  chapterId: string,
  tier: Tier,
  lang: Lang,
): Promise<VocabCard[]> {
  const rows = await selectVocab((cols) =>
    supabase
      .from("pe_vocabulary_terms")
      .select(cols)
      .eq("chapter_id", Number(chapterId))
      .eq("tier", tier)
      .eq("role", "apply")
      .order("display_order", { ascending: true }),
  );

  return rows.map((r) => ({
    id: r.id,
    word: conceptHeadword(lang, r.pe_concepts),
    definition: lang === "es" ? r.definition_es : r.definition_en,
    example: lang === "es" ? r.example_sentence_es : r.example_sentence_en,
    imageUrl: r.image_url ? peSceneAssetUrl(r.image_url) : null,
  }));
}

/** A Launch vocab card — teacher-only (role='launch'), tier-tagged for grouping. */
export interface VocabLaunchCard extends VocabCard {
  tier: Tier;
}

/**
 * Vocabulary — LAUNCH cards (role='launch'): the teacher-only set, shown in the
 * doc-hub Vocab Launch tab. ALL tiers for the chapter (not student-tier scoped),
 * bilingual in-row. Never a StudentAssetType — never exposable via the picker.
 */
export async function fetchVocabLaunch(
  chapterId: string,
  lang: Lang,
): Promise<VocabLaunchCard[]> {
  const rows = await selectVocab((cols) =>
    supabase
      .from("pe_vocabulary_terms")
      .select(cols)
      .eq("chapter_id", Number(chapterId))
      .eq("role", "launch")
      .order("tier", { ascending: true })
      .order("display_order", { ascending: true }),
  );

  return rows.map((r) => ({
    id: r.id,
    tier: r.tier as Tier,
    word: conceptHeadword(lang, r.pe_concepts),
    definition: lang === "es" ? r.definition_es : r.definition_en,
    example: lang === "es" ? r.example_sentence_es : r.example_sentence_en,
    imageUrl: r.image_url ? peSceneAssetUrl(r.image_url) : null,
  }));
}

// ---------------------------------------------------------------------------
// Episode — one Vimeo video per chapter+tier+lang. Do NOT gate on is_published.
// ---------------------------------------------------------------------------

export interface EpisodeInfo {
  vimeoId: string | null;
}

export async function fetchEpisode(
  chapterId: string,
  tier: Tier,
  lang: Lang,
): Promise<EpisodeInfo | null> {
  const { data, error } = await supabase
    .from("pe_episodes")
    .select("vimeo_id")
    .eq("chapter_id", Number(chapterId))
    .eq("tier", tier)
    .eq("language", lang)
    .maybeSingle();
  if (error) throw error;
  return data ? { vimeoId: (data as any).vimeo_id ?? null } : null;
}

// Chapter slug ("s1-ch01") → pe_chapters.id. The public curriculum catalog is
// keyed by slug; fetchEpisode needs the numeric id. RLS-gated (signed-in only).
export async function fetchChapterIdBySlug(slug: string): Promise<string | null> {
  const { data, error } = await supabase
    .from("pe_chapters")
    .select("id")
    .eq("slug", slug)
    .maybeSingle();
  if (error) throw error;
  return data ? String((data as { id: number }).id) : null;
}

// ---------------------------------------------------------------------------
// Activity — student-facing view only: title + description. The teacher
// facilitation markdown (variant_20/30/45, materials_*) is deliberately NOT
// selected here — it is teacher-only (reserved for the Phase-6 doc-hub).
// ---------------------------------------------------------------------------

export interface ActivityInfo {
  title: string | null;
  description: string | null;
}

export async function fetchActivity(chapterId: string): Promise<ActivityInfo | null> {
  const { data, error } = await supabase
    .from("pe_activities")
    .select("activity_title, activity_description")
    .eq("chapter_id", Number(chapterId))
    .maybeSingle();
  if (error) throw error;
  return data
    ? {
        title: (data as any).activity_title ?? null,
        description: (data as any).activity_description ?? null,
      }
    : null;
}
